/**
 * Insurance content per `insurance_data_enum`.
 *
 * EACH VARIANT IS FULLY SELF-CONTAINED.
 * ─────────────────────────────────────
 * Covered risk texts (titles, descriptions, topics), raffle text, and card
 * amounts are duplicated in every variant on purpose. Editing one variant
 * never affects another, so there is no risk of an accidental cross-variant
 * change. Search by variant name to find what to edit; copy an existing
 * variant block to start a new one.
 *
 * If a variant doesn't list a given risk key, `resolveCoverage` falls back to
 * `standard`. So per-variant entries only need the keys that actually differ
 * from `standard` (or the keys that variant cares about).
 *
 * To add a brand-new risk key (e.g. `new_coverage_key`):
 *   1. Add it to `standard.coverages` so the fallback works for every variant.
 *   2. Override per-variant where needed.
 */

import { ComponentType } from 'react';
import { HeartIcon, Accessibility1Icon, HeartCheckmarkIcon, BriefcaseIcon } from '@qitech/icons';

// ── Types ────────────────────────────────────────────────────────────────

export type CoverageTopic = { title: string; description: string };
export type CoverageEntry = { title: string; description: string; topics?: CoverageTopic[] };

export type BenefitCardKey = 'death' | 'disability' | 'critical_illness' | 'unemployment';

export interface RaffleBenefit { prefix: string; highlight: string }
export interface BenefitAmount { prefix?: string; highlight: string }

export interface InsuranceVariant {
    raffle: RaffleBenefit | null;
    coverages: Partial<Record<string, CoverageEntry>>;
    cardAmounts: Partial<Record<BenefitCardKey, BenefitAmount>>;
}

export interface BenefitCardMeta {
    description: string;
    risks: string[];
    Icon: ComponentType<{ size?: number }>;
}

// ── Benefit cards ────────────────────────────────────────────────────────
//
// Defines the four benefit-summary cards shown on the resume screen, the
// risk keys each card aggregates, and the icon used for it. To add a new
// card: add a key to `BenefitCardKey`, add an entry below, and ensure the
// risk keys here exist in `standard.coverages`.

export const BENEFIT_CARD_META: Record<BenefitCardKey, BenefitCardMeta> = {
    death: {
        description: 'para morte natural ou acidental',
        risks: ['death', 'accidental_death', 'mqc_death', 'accidental_death_fgts'],
        Icon: HeartIcon,
    },
    disability: {
        description: 'para invalidez total por acidente',
        risks: ['permanent_disability', 'permanent_disability_illness', 'permanent_disability_fgts', 'ipta_disability'],
        Icon: Accessibility1Icon,
    },
    critical_illness: {
        description: 'para doenças graves',
        risks: ['critical_illness'],
        Icon: HeartCheckmarkIcon,
    },
    unemployment: {
        description: 'para desemprego involuntário',
        risks: ['unemployment', 'involuntary_unemployment', 'income_loss_fgts'],
        Icon: BriefcaseIcon,
    },
};

// ── Variants ─────────────────────────────────────────────────────────────

const INSURANCE: Record<string, InsuranceVariant> = {

    // ──────────────────────────────────────────────────────────────────────
    // standard — fallback used when a variant doesn't define a key.
    // ──────────────────────────────────────────────────────────────────────
    standard: {
        raffle: null,
        coverages: {
            accidental_death: {
                title: 'Morte por acidente',
                description: 'A morte do segurado, sempre que esta esteja relacionada a causas acidentais.',
            },
            death: {
                title: 'Morte',
                description: 'Sua família recebe até R$ 20.000 se você falecer.',
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
            },
            accidental_death_fgts: {
                title: 'Morte por acidente',
                description: 'Morte do segurado, decorrente de causas acidentais ou acidente pessoal ocorrido durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Suicídio: 2 (dois) anos ininterruptos, contados a partir do início de vigência do 1º seguro. Caso exista um período intermediário sem seguro, a carência será reiniciada.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 5.000,00 ou 2x o valor do empréstimo contratado, o que for maior, limitado a R$ 13.320,00 por apólice e R$ 40.000,00 por CPF.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez acidental',
                description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas ocorridas por causas acidentais.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
            },
            permanent_disability_illness: {
                title: 'Invalidez permanente total por acidente ou doença',
                description: 'Receba até R$ 20.000 se um acidente grave prejudicar sua rotina.',
            },
            permanent_disability_fgts: {
                title: 'Invalidez por acidente',
                description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 5.000,00 ou 2x o valor do empréstimo contratado, o que for maior, limitado a R$ 13.320,00 por apólice e R$ 40.000,00 por CPF.' },
                ],
            },
            critical_illness: {
                title: 'Doenças graves',
                description: 'Cobertura de até R$ 20.000 para ajudar no seu tratamento.',
            },
            unemployment: {
                title: 'Ajuda no desemprego',
                description: 'Até 6 pagamentos de R$ 1.000 se perder o emprego involuntariamente (demissão sem justa causa).',
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
            },
            income_loss_fgts: {
                title: 'Perda de Renda',
                description: 'Perda de salário do segurado por desemprego involuntário (CLT). Perda de renda do segurado por incapacidade física total e temporária (autônomo).',
                topics: [
                    { title: 'Carência', description: '30 dias. Caso exista um período intermediário sem seguro, a vigência será reiniciada.' },
                    { title: 'Franquia', description: '15 dias.' },
                    { title: 'Capital segurado', description: 'Valor do empréstimo contratado pelo segurado, limitado a R$ 4.000 por apólice ou no agregado do CPF, indenizável em pagamento único.' },
                ],
            },
            funeral_assistance: {
                title: 'Auxílio Funeral',
                description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.',
            },
            medical_assistance: {
                title: 'Assistência em Medicamentos',
                description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.',
            },
            funeral_assistance_consignated: {
                title: 'Auxílio funeral',
                description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.',
            },
            medical_assistance_consignated: {
                title: 'Assistência em Medicamentos',
                description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.',
            },
            food_basket: {
                title: 'Auxílio Cesta Básica',
                description: 'Auxílio alimentação pago ao beneficiário em caso de falecimento do segurado por causas naturais ou acidentais.',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 20.000' },
            disability: { prefix: 'até', highlight: 'R$ 20.000' },
            critical_illness: { prefix: 'até', highlight: 'R$ 20.000' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich1
    // ──────────────────────────────────────────────────────────────────────
    Zurich1: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {},
        cardAmounts: {},
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich12 — 12-month plan (Produto 7): death/accidental/IPTA up to R$ 2.000.
    // No illness disability, no unemployment. Monthly raffle of R$ 10.000.
    // ──────────────────────────────────────────────────────────────────────
    Zurich12: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {
            death: {
                title: 'Morte natural',
                description: 'Sua família recebe até R$ 2.000 se você falecer por causas naturais.',
            },
            accidental_death: {
                title: 'Morte por acidente',
                description: 'Sua família recebe até R$ 2.000 se você falecer em decorrência de um acidente.',
            },
            permanent_disability: {
                title: 'Invalidez permanente total por acidente',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 2.000' },
            disability: { prefix: 'até', highlight: 'R$ 2.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich18 — 18-month plan (Produto 6): death/accidental/IPTA up to R$ 2.000.
    // Unemployment: up to 3x R$ 150. No illness disability. Raffle R$ 10.000.
    // ──────────────────────────────────────────────────────────────────────
    Zurich18: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {
            death: {
                title: 'Morte natural',
                description: 'Sua família recebe até R$ 2.000 se você falecer por causas naturais.',
            },
            accidental_death: {
                title: 'Morte por acidente',
                description: 'Sua família recebe até R$ 2.000 se você falecer em decorrência de um acidente.',
            },
            permanent_disability: {
                title: 'Invalidez permanente total por acidente',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 3 pagamentos de até R$ 150 em caso de desemprego involuntário (demissão sem justa causa).',
            },
            involuntary_unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 3 pagamentos de até R$ 150 em caso de desemprego involuntário (demissão sem justa causa).',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 2.000' },
            disability: { prefix: 'até', highlight: 'R$ 2.000' },
            unemployment: { prefix: 'até', highlight: '3x de R$ 150' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich24 — 24-month plan (Produto 5): death/accidental/IPTA/illness
    // disability up to R$ 2.000. Unemployment: up to 4x R$ 200. Raffle R$ 10.000.
    // ──────────────────────────────────────────────────────────────────────
    Zurich24: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {
            death: {
                title: 'Morte natural',
                description: 'Sua família recebe até R$ 2.000 se você falecer por causas naturais.',
            },
            accidental_death: {
                title: 'Morte por acidente',
                description: 'Sua família recebe até R$ 2.000 se você falecer em decorrência de um acidente.',
            },
            permanent_disability: {
                title: 'Invalidez permanente total por acidente',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            permanent_disability_illness: {
                title: 'Invalidez permanente total por doença',
                description: 'Receba até R$ 2.000 em caso de invalidez permanente total decorrente de doença.',
            },
            unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 4 pagamentos de até R$ 200 em caso de desemprego involuntário (demissão sem justa causa).',
            },
            involuntary_unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 4 pagamentos de até R$ 200 em caso de desemprego involuntário (demissão sem justa causa).',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 2.000' },
            disability: { prefix: 'até', highlight: 'R$ 2.000' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 200' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich36 — 36-month plan (Produto 4): death/accidental/IPTA/illness
    // disability up to R$ 5.000. Unemployment: up to 6x R$ 200. Raffle R$ 10.000.
    // ──────────────────────────────────────────────────────────────────────
    Zurich36: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {
            death: {
                title: 'Morte natural',
                description: 'Sua família recebe até R$ 5.000 se você falecer por causas naturais.',
            },
            accidental_death: {
                title: 'Morte por acidente',
                description: 'Sua família recebe até R$ 5.000 se você falecer em decorrência de um acidente.',
            },
            permanent_disability: {
                title: 'Invalidez permanente total por acidente',
                description: 'Receba até R$ 5.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Receba até R$ 5.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            permanent_disability_illness: {
                title: 'Invalidez permanente total por doença',
                description: 'Receba até R$ 5.000 em caso de invalidez permanente total decorrente de doença.',
            },
            unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 6 pagamentos de até R$ 200 em caso de desemprego involuntário (demissão sem justa causa).',
            },
            involuntary_unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 6 pagamentos de até R$ 200 em caso de desemprego involuntário (demissão sem justa causa).',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 5.000' },
            disability: { prefix: 'até', highlight: 'R$ 5.000' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 200' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Zurich48 — 48-month plan (Produto 3): death/accidental/IPTA/illness
    // disability up to R$ 7.000. Unemployment: up to 6x R$ 300. Raffle R$ 10.000.
    // ──────────────────────────────────────────────────────────────────────
    Zurich48: {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {
            death: {
                title: 'Morte natural',
                description: 'Sua família recebe até R$ 7.000 se você falecer por causas naturais.',
            },
            accidental_death: {
                title: 'Morte por acidente',
                description: 'Sua família recebe até R$ 7.000 se você falecer em decorrência de um acidente.',
            },
            permanent_disability: {
                title: 'Invalidez permanente total por acidente',
                description: 'Receba até R$ 7.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Receba até R$ 7.000 em caso de invalidez permanente total decorrente de acidente.',
            },
            permanent_disability_illness: {
                title: 'Invalidez permanente total por doença',
                description: 'Receba até R$ 7.000 em caso de invalidez permanente total decorrente de doença.',
            },
            unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 6 pagamentos de até R$ 300 em caso de desemprego involuntário (demissão sem justa causa).',
            },
            involuntary_unemployment: {
                title: 'Desemprego involuntário',
                description: 'Até 6 pagamentos de até R$ 300 em caso de desemprego involuntário (demissão sem justa causa).',
            },
        },
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 7.000' },
            disability: { prefix: 'até', highlight: 'R$ 7.000' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 300' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // 180_seguros
    // ──────────────────────────────────────────────────────────────────────
    '180_seguros': {
        raffle: { prefix: 'Sorteios mensais de', highlight: 'R$ 10.000,00' },
        coverages: {},
        cardAmounts: {
            death: { prefix: 'até', highlight: 'R$ 13.320' },
            disability: { prefix: 'até', highlight: 'R$ 13.320' },
            unemployment: { prefix: 'até', highlight: 'R$ 4.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Akad_Seguros1 — 4 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Akad_Seguros1: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Akad_Seguros2 — 5 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Akad_Seguros2: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Akad_Seguros3 — 6 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Akad_Seguros3: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Ezze1 — 4 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Ezze1: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Ezze2 — 5 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Ezze2: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Ezze3 — 6 installments of unemployment
    // ──────────────────────────────────────────────────────────────────────
    Ezze3: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // Blindado1 — no raffle, no unemployment coverage
    // ──────────────────────────────────────────────────────────────────────
    Blindado1: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_cover_4 — Ezze, 2 parcelas of unemployment (max R$ 2.000)
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_cover_4: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '2x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_cover_6 — Ezze, 3 parcelas of unemployment (max R$ 3.000)
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_cover_6: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 3 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 3 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '3x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_cover_8 — Ezze, 4 parcelas of unemployment (max R$ 4.000)
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_cover_8: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_cover_10 — Ezze, 5 parcelas of unemployment (max R$ 5.000)
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_cover_10: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_cover_12 — Ezze, 6 parcelas of unemployment (max R$ 6.000)
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_cover_12: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_qi_hub_cover_4 — QI Hub (same coverages as Ezze), 2 parcelas of unemployment (max R$ 2.000)
    // ──────────────────────────────────────────────────────────────────────
    private_qi_hub_cover_4: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '2x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_qi_hub_cover_6 — QI Hub (same coverages as Ezze), 3 parcelas of unemployment (max R$ 3.000)
    // ──────────────────────────────────────────────────────────────────────
    private_qi_hub_cover_6: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 3 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 3 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '3x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_qi_hub_cover_8 — QI Hub (same coverages as Ezze), 4 parcelas of unemployment (max R$ 4.000)
    // ──────────────────────────────────────────────────────────────────────
    private_qi_hub_cover_8: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_qi_hub_cover_10 — QI Hub (same coverages as Ezze), 5 parcelas of unemployment (max R$ 5.000)
    // ──────────────────────────────────────────────────────────────────────
    private_qi_hub_cover_10: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_qi_hub_cover_12 — QI Hub (same coverages as Ezze), 6 parcelas of unemployment (max R$ 6.000)
    // ──────────────────────────────────────────────────────────────────────
    private_qi_hub_cover_12: {
        raffle: { prefix: 'Sorteio mensal de', highlight: 'R$ 5.000,00' },
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural e 2 anos ininterruptos para suicídio.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_credit_insurance_food_basket_2 — Ezze, prestamista com Auxílio Cesta Básica (Produto 01):
    // death/IPTA up to R$ 20.000, 2 parcelas of unemployment (R$ 500 each), cesta R$ 1.500.
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_credit_insurance_food_basket_2: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            food_basket: {
                title: 'Auxílio Cesta Básica (AXCB-M)',
                description: 'Indenização a título de auxílio alimentação ao beneficiário em caso de falecimento do segurado por causas naturais (doença) ou acidente pessoal, ocorrido durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 1.500,00.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '2x de R$ 500' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_credit_insurance_food_basket_4 — Ezze, prestamista com Auxílio Cesta Básica (Produto 02):
    // death/IPTA up to R$ 25.000, 4 parcelas of unemployment (R$ 500 each), cesta R$ 1.500.
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_credit_insurance_food_basket_4: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 25.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 25.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 25.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 25.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            food_basket: {
                title: 'Auxílio Cesta Básica (AXCB-M)',
                description: 'Indenização a título de auxílio alimentação ao beneficiário em caso de falecimento do segurado por causas naturais (doença) ou acidente pessoal, ocorrido durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 1.500,00.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 500' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_ezze_credit_insurance_food_basket_6 — Ezze, prestamista com Auxílio Cesta Básica (Produto 03):
    // death/IPTA up to R$ 30.000, 6 parcelas of unemployment (R$ 500 each), cesta R$ 1.500.
    // ──────────────────────────────────────────────────────────────────────
    private_ezze_credit_insurance_food_basket_6: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            mqc_death: {
                title: 'Morte',
                description: 'Morte do segurado, decorrente de causas naturais ou acidentais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            ipta_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Quitação do saldo devedor, limitado a R$ 30.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            involuntary_unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 500,00.' },
                    { title: 'Observação', description: 'Exclusivo para CLT. Exige 9 meses consecutivos e ininterruptos no atual empregador na data da rescisão. Na adesão, o segurado não pode estar cumprindo aviso prévio.' },
                ],
            },
            food_basket: {
                title: 'Auxílio Cesta Básica (AXCB-M)',
                description: 'Indenização a título de auxílio alimentação ao beneficiário em caso de falecimento do segurado por causas naturais (doença) ou acidente pessoal, ocorrido durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias, exceto se decorrente de acidente.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'R$ 1.500,00.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 500' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p1 — 180 Seguros, prestamista consignado privado -  0 parcelas of unemployment
    // ──────────────────────────────────────────────────────────────────────
    private_180_p1: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            }
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p2 — 180 Seguros, prestamista consignado privado -  2 parcelas of unemployment (max R$ 2.000)
    // ──────────────────────────────────────────────────────────────────────
    private_180_p2: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 2 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '2x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p3 — 180 Seguros, prestamista consignado privado -  3 parcelas of unemployment (max R$ 3.000)
    // ──────────────────────────────────────────────────────────────────────
    private_180_p3: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 3 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '3x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p4 — 180 Seguros, prestamista consignado privado -  4 parcelas of unemployment (max R$ 4.000)
    // ──────────────────────────────────────────────────────────────────────
    private_180_p4: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 4 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '4x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p5 — 180 Seguros, prestamista consignado privado -  5 parcelas of unemployment (max R$ 5.000)
    // ──────────────────────────────────────────────────────────────────────
    private_180_p5: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 5 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '5x de R$ 1.000' },
        },
    },

    // ──────────────────────────────────────────────────────────────────────
    // private_180_p6 — 180 Seguros, prestamista consignado privado -  6 parcelas of unemployment (max R$ 6.000)
    // ──────────────────────────────────────────────────────────────────────
    private_180_p6: {
        raffle: null,
        coverages: {
            death: {
                title: 'Morte Qualquer Causa (MQC)',
                description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: '30 dias para morte natural.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            permanent_disability: {
                title: 'Invalidez Permanente Total por Acidente (IPTA)',
                description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
                topics: [
                    { title: 'Carência', description: 'Não há.' },
                    { title: 'Franquia', description: 'Não há.' },
                    { title: 'Capital segurado', description: 'Saldo devedor, limitado a R$ 20.000,00 por apólice.' },
                ],
            },
            unemployment: {
                title: 'Desemprego Involuntário (CLT)',
                description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário.',
                topics: [
                    { title: 'Carência', description: '30 dias' },
                    { title: 'Franquia', description: '30 dias' },
                    { title: 'Capital segurado', description: 'Até 6 parcelas de até R$ 1.000,00.' },
                    { title: 'Observação', description: 'Exige comprovação de 12 meses de renda contínua no momento do sinistro.' },
                ],
            },
        },
        cardAmounts: {
            death: { highlight: 'Saldo Devedor' },
            disability: { highlight: 'Saldo Devedor' },
            unemployment: { prefix: 'até', highlight: '6x de R$ 1.000' },
        },
    },
};

// ── Resolvers ────────────────────────────────────────────────────────────

const variantFor = (ins: string): InsuranceVariant => INSURANCE[ins] ?? INSURANCE.standard;

export const resolveCoverage = (risk: string, ins: string): CoverageEntry | null =>
    variantFor(ins).coverages[risk] ?? INSURANCE.standard.coverages[risk] ?? null;

export const resolveRaffle = (ins: string): RaffleBenefit | null => variantFor(ins).raffle;

export const resolveBenefitAmount = (card: BenefitCardKey, ins: string): BenefitAmount | null =>
    variantFor(ins).cardAmounts[card] ?? INSURANCE.standard.cardAmounts[card] ?? null;

// ── Account terms ────────────────────────────────────────────────────────
//
// Bullet points shown in the "internal account" terms card (when the
// insurance payment_type is `internal_account`). Edit text or order here.

export const ACCOUNT_TERM_ITEMS: ReadonlyArray<{ label: string; text: string }> = [
    {
        label: 'Custo Zero',
        text: 'Você não paga nenhuma taxa de abertura ou manutenção.',
    },
    {
        label: 'Uso Exclusivo',
        text: 'Serve apenas para movimentar valores do seu seguro (pagamentos e indenizações).',
    },
    {
        label: 'Organização',
        text: 'Seus recursos ficam protegidos e separados de outras contas.',
    },
    {
        label: 'Praticidade',
        text: 'Tudo acontece de forma automática e transparente, conforme as normas do BACEN e SUSEP.',
    },
];

// ── Risk ordering ────────────────────────────────────────────────────────
//
// Display order for the covered_risks list. Lower number = shown first.
// Risks not listed here fall to the bottom (priority 1000).

const RISK_PRIORITY: Record<string, number> = {
    permanent_disability_illness: 2,
    permanent_disability_fgts: 2,
    permanent_disability: 2,
    critical_illness: 3,
    death: 4,
    unemployment: 1,
    involuntary_unemployment: 1,
    income_loss_fgts: 5,
};

export const filterAndSortRisks = (risks: string[] | undefined): string[] => {
    if (!risks || !Array.isArray(risks)) return [];
    return risks.sort((a, b) => (RISK_PRIORITY[a] ?? 1000) - (RISK_PRIORITY[b] ?? 1000));
};

export const getActiveBenefitCards = (risks: string[] | undefined, ins: string): BenefitCardKey[] => {
    if (!risks || !Array.isArray(risks)) return [];
    const risksSet = new Set(risks);
    return (Object.keys(BENEFIT_CARD_META) as BenefitCardKey[]).filter((k) =>
        BENEFIT_CARD_META[k].risks.some((r) => risksSet.has(r)) && resolveBenefitAmount(k, ins) !== null,
    );
};
